import { useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { showError, showSuccess } from '@utils';
import { selectLoggedInUserState } from '@store';
import { subscriptionsServices, communicationServices } from '@services';

import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees, useInfinitePagination } from 'hooks/common';

import { TAB_KEYS } from 'components/subscriptions/SubscriptionTabContent/constants';
import { useHistoryItemsChangelog } from 'hooks/historyItems';
import {
  ENTITY_TYPES_COMMENTS,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { useMailBoxesOperations } from 'hooks/mailBoxes';
import { DEFAULT_MAILBOX } from 'hooks/communication/constants';
import {
  handleChangeLog,
  handleEmailHistory,
  handleInfiniteQuerySubscriptionData,
  handleSMS,
  handleSubscriptionComments,
} from './utils';
import { SUBSCRIPTION_HISTORY_CHANGES_DEFAULT_PAGE_SIZE } from './constants';

export const useSubscriptionHistoryOperations = ({
  subscriptionInternalId,
  ownerEmail,
}) => {
  const router = useRouter();

  const loggedInUser = useSelector(selectLoggedInUserState);

  const {
    assignees,
    isFetched: isAssigneesFetched,
    getAssigneeName,
  } = useAssignees();

  const { isSystemActor, handleChangelogItemFieldName } =
    useHistoryItemsChangelog();
  const { mailBoxes } = useMailBoxesOperations();

  const subscriptionId = router.query?.subscriptionId;

  const agent = assignees?.find((item) => item.email === loggedInUser?.email);

  const isHistoryTab = router.query?.tab === TAB_KEYS.HISTORY;

  const isHistoryTabComment =
    isHistoryTab && router.query?.historyTab === 'comment';
  const isHistoryTabChangelog =
    isHistoryTab && router.query?.historyTab === 'changelog';
  const isHistoryTabEmail =
    isHistoryTab && router.query?.historyTab === 'email';
  const isHistoryTabSms = isHistoryTab && router.query?.historyTab === 'sms';
  const isHistoryTabAll =
    isHistoryTab &&
    (!router.query?.historyTab || router.query?.historyTab === 'all');

  const emailMutation = useMutation(communicationServices.replyEmailV2);

  const selectedMailbox =
    mailBoxes?.find((mailbox) => mailbox?.address === DEFAULT_MAILBOX) ||
    mailBoxes?.[0] ||
    null;

  const changelogResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_HISTORY_CHANGELOG_KEY, subscriptionId],
    () =>
      subscriptionsServices?.getSubscriptionChangeLogHistoryById(
        subscriptionId
      ),
    {
      enabled:
        isHistoryTab &&
        isHistoryTabChangelog &&
        !!subscriptionId &&
        isAssigneesFetched,
    }
  );

  const emailResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_HISTORY_EMAIL_KEY, subscriptionId],
    () =>
      subscriptionsServices?.getSubscriptionEmailHistoryById(subscriptionId),
    {
      enabled:
        isHistoryTab &&
        isHistoryTabEmail &&
        !!subscriptionId &&
        isAssigneesFetched,
    }
  );

  const commentsResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_COMMENTS_KEY, subscriptionId],
    () => subscriptionsServices.getSubscriptionComments(subscriptionId),
    {
      enabled:
        isHistoryTab &&
        isHistoryTabComment &&
        !!subscriptionId &&
        isAssigneesFetched,
    }
  );

  const smsResponse = useQuery(
    [QUERY_KEYS.GET_SUBSCRIPTION_HISTORY_SMS_KEY, subscriptionId],
    () =>
      subscriptionsServices.getSubscriptionSms({
        params: { page: 1, pageSize: 500, order: 'desc', orderBy: 'createdAt' },
        payload: {
          type: 0,
          value: [
            {
              field: 'entity_id',
              operator: FILTER_OPERATOR_ENUMS['='],
              type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
              value: [subscriptionInternalId],
            },
          ],
        },
      }),
    {
      enabled:
        isHistoryTab &&
        isHistoryTabSms &&
        !!subscriptionId &&
        isAssigneesFetched &&
        !!subscriptionInternalId,
    }
  );

  const normalizedChangelog = useMemo(
    () =>
      handleChangeLog({
        changelog: changelogResponse?.data?.data || [],
        getAssigneeName,
      }),
    [changelogResponse?.data?.data, getAssigneeName]
  );

  const normalizedEmailHistory = useMemo(
    () =>
      handleEmailHistory({
        emailHistory: emailResponse?.data?.data || [],
        getAssigneeName,
      }),
    [emailResponse?.data?.data, getAssigneeName]
  );

  const normalizedComments = useMemo(() => {
    return handleSubscriptionComments({
      data: commentsResponse?.data?.data || [],
      loggedInUser,
      getAssigneeName,
    });
  }, [commentsResponse?.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCommentMutation = useMutation(
    subscriptionsServices.addSubscriptionComment
  );

  const deleteCommentMutation = useMutation(
    subscriptionsServices.deleteSubscriptionComment
  );

  const normalizedSMS = useMemo(
    () =>
      handleSMS({
        sms: smsResponse?.data?.data || [],
        getAssigneeName,
      }),
    [smsResponse?.data?.data, getAssigneeName]
  );

  const subscriptionHistoryParams = useMemo(
    () => ({
      subscriptionId,
      pageSize: SUBSCRIPTION_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
    }),
    [subscriptionId]
  );

  const subscriptionHistoryInfiniteQuery = useInfinitePagination({
    queryKey: QUERY_KEYS.GET_SUBSCRIPTION_HISTORY_CHANGES_KEY,
    params: subscriptionHistoryParams,
    enabled: Boolean(subscriptionId) && isHistoryTab && isHistoryTabAll,
    initialPage: 1,
    queryFn: subscriptionsServices.getSubscriptionHistoryChanges,
  });

  const handleSubscriptionAddComment = useCallback(
    (commentData) => {
      addCommentMutation.mutate(
        {
          entityId: subscriptionId,
          entityType: ENTITY_TYPES_COMMENTS.subscriptions,
          comment: commentData.content,
        },
        {
          onSuccess: () => {
            commentsResponse.refetch();
          },
          onError: (error) => {
            showError(error);
          },
        }
      );
    },
    [subscriptionId, addCommentMutation, commentsResponse]
  );

  const handleSubscriptionDeleteComment = useCallback(
    (commentItem) => {
      const commentId = commentItem?.id || commentItem?.resourceId;
      if (!commentId) return;

      deleteCommentMutation.mutate(commentId, {
        onSuccess: () => {
          commentsResponse.refetch();
          if (isHistoryTabAll) subscriptionHistoryInfiniteQuery.refetch();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [
      commentsResponse,
      deleteCommentMutation,
      subscriptionHistoryInfiniteQuery,
      isHistoryTabAll,
    ]
  );

  const subscriptionHistoryQueryItemsHandled = useMemo(() => {
    return handleInfiniteQuerySubscriptionData({
      data: subscriptionHistoryInfiniteQuery?.items,
      loggedInUser,
      handleChangelogItem: handleChangelogItemFieldName,
      isSystemActor,
      getAssigneeName,
    });
  }, [
    subscriptionHistoryInfiniteQuery?.items,
    loggedInUser,
    handleChangelogItemFieldName,
    isSystemActor,
    getAssigneeName,
  ]);

  const subscriptionHistoryQueryData = useMemo(() => {
    if (isHistoryTabChangelog) {
      return {
        ...changelogResponse,
        items: normalizedChangelog,
      };
    }
    if (isHistoryTabEmail) {
      return {
        ...emailResponse,
        items: normalizedEmailHistory,
      };
    }
    if (isHistoryTabComment) {
      return {
        ...commentsResponse,
        items: normalizedComments,
      };
    }
    if (isHistoryTabSms) {
      return {
        ...smsResponse,
        items: normalizedSMS,
      };
    }

    return {
      ...subscriptionHistoryInfiniteQuery,
      items: subscriptionHistoryQueryItemsHandled,
    };
  }, [
    isHistoryTabChangelog,
    isHistoryTabEmail,
    changelogResponse,
    emailResponse,
    normalizedEmailHistory,
    normalizedChangelog,
    isHistoryTabComment,
    commentsResponse,
    normalizedComments,
    subscriptionHistoryInfiniteQuery,
    subscriptionHistoryQueryItemsHandled,
    isHistoryTabSms,
    smsResponse,
    normalizedSMS,
  ]);

  const handleSendMailSubscription = useCallback(
    (payload) => {
      const formData = new FormData();
      formData.append('from', payload.from);
      formData.append('body', payload.body);
      formData.append('subject', payload.subject);
      formData.append('mailboxId', payload.mailboxId);
      formData.append('entityId', subscriptionId);
      formData.append('entityType', 'subscription');
      formData.append('id', payload.id);

      formData.append('cc', JSON.stringify(payload.cc));

      if (payload.bcc && payload.bcc.length > 0) {
        formData.append('bcc', JSON.stringify(payload.bcc));
      }

      if (payload.to && payload.to.length > 0) {
        formData.append('to', JSON.stringify(payload.to));
      }

      if (payload.attachments && payload.attachments.length > 0) {
        payload.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }
      // send existing attachment ids from the template email (if any)
      if (payload.attachmentIds && payload.attachmentIds.length > 0) {
        formData.append('attachmentIds', JSON.stringify(payload.attachmentIds));
      }

      if (payload.templateId) {
        formData.append('templateId', payload.templateId);
      }
      if (payload.templateLang) {
        formData.append('templateLang', payload.templateLang);
      }

      return emailMutation.mutateAsync(formData, {
        onSuccess: () => {
          showSuccess('Email sent successfully!');
          emailResponse.refetch();
          if (isHistoryTabAll) subscriptionHistoryInfiniteQuery.refetch();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [
      subscriptionId,
      emailMutation,
      emailResponse,
      subscriptionHistoryInfiniteQuery,
      isHistoryTabAll,
    ]
  );

  const subscriptionHistoryQuery = useMemo(() => {
    return {
      agent,
      ...subscriptionHistoryQueryData,
      onSendMail: handleSendMailSubscription,
      onAddComment: handleSubscriptionAddComment,
      onDeleteComment: handleSubscriptionDeleteComment,
      selectedMailboxId: selectedMailbox?.id,
      selectedMailboxAddress: selectedMailbox?.address,
      initialReceivers: ownerEmail ? [ownerEmail] : [],
    };
  }, [
    agent,
    subscriptionHistoryQueryData,
    handleSendMailSubscription,
    handleSubscriptionAddComment,
    handleSubscriptionDeleteComment,
    selectedMailbox,
    ownerEmail,
  ]);

  useEffect(() => {
    if (changelogResponse?.isError) {
      showError(changelogResponse?.error);
    }
    if (emailResponse?.isError) {
      showError(emailResponse?.error);
    }
    if (commentsResponse?.isError) {
      showError(commentsResponse?.error);
    }
    if (smsResponse?.isError) {
      showError(smsResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changelogResponse?.isError,
    emailResponse?.isError,
    commentsResponse?.isError,
    smsResponse?.isError,
  ]);

  return {
    subscriptionHistoryQuery,
  };
};
